import { CONTAINER_HEIGHT_IN_MMM, TCG_IN_MMM } from "../consts";
import { IRowPattern, ITierPattern } from "../types/IPositionPatterns";

import { ITiersByRow } from "../getRowsAndTiersFromSlotKeys";
import { pad2 } from "@tedivo/tedivo-pure-helpers";

/**
 * Resolved (already store/master-fallback-applied) values for a single row.
 * The caller is responsible for the resolution order (e.g. per-row override
 * ?? common override ?? master CG), since that precedence is app-specific.
 */
export interface IAftBayRowInput {
  /** Transversal Center of Gravity of the row, in mm */
  tcg?: number;
  /** Vertical Center of Gravity of the bottom of the row's stack, in mm */
  bottomBase?: number;
  /** Maximum allowed stack height (ceiling) for the row, in mm */
  maxHeight?: number;
}

export interface IAftBayLayoutRow {
  row: IRowPattern;
  /** Even row numbers are Port */
  isPort: boolean;
  tcg: number;
  /** mm midpoint (or outer edge, for the first/last row) with the previous row */
  prevTcgMid: number;
  /** mm midpoint (or outer edge, for the first/last row) with the next row */
  nextTcgMid: number;
  bottomBaseMm: number;
  maxHeightMm: number | undefined;
  bottomTier: ITierPattern;
  numTiers: number;
  /** Absolute mm base (VCG) of each stacked tier, bottom tier first */
  tierBasesMm: number[];
}

export interface IAftBayLayout {
  /** Total mm width from Port outer edge to Starboard outer edge */
  widthMm: number;
  minBB: number;
  maxBB: number;
  /** mm offset to add to a TCG to make it non-negative (Port outer edge = 0) */
  addXMm: number;
  /** Only rows that have both slot tiers and a resolved bottom base, in the input order */
  rows: IAftBayLayoutRow[];
}

/**
 * Calculates the physical (mm) layout of a bay's aft/cross-section diagram: row
 * positions by TCG and tier stacks by bottom base + container height. Contains no
 * SVG/DOM/pixel concerns - see `drawAftBayDiagram` (tedivo-bay-grid-core) for drawing.
 */
export function calculateAftBayLayout({
  rows,
  tiersByRow,
  perRow,
  getTierHeightMm = () => CONTAINER_HEIGHT_IN_MMM,
}: {
  /** All rows of the bay, Port to Starboard */
  rows: IRowPattern[];
  tiersByRow: ITiersByRow;
  perRow: Record<string, IAftBayRowInput | undefined>;
  /** Height in mm of the tier at `tierIndex` (0 = bottom) of `row`. Defaults to a constant 8.5ft (CONTAINER_HEIGHT_IN_MMM) */
  getTierHeightMm?: (row: IRowPattern, tierIndex: number) => number;
}): IAftBayLayout | undefined {
  if (rows.length === 0) return undefined;

  const portRow = rows[0];
  const stbdRow = rows[rows.length - 1];

  const portTcg = perRow[portRow]?.tcg;
  const stbdTcg = perRow[stbdRow]?.tcg;

  if (portTcg === undefined || stbdTcg === undefined) return undefined;

  const widthMm = stbdTcg - portTcg + TCG_IN_MMM;
  if (isNaN(widthMm)) return undefined;

  const addXMm = TCG_IN_MMM * 0.5 + Math.abs(portTcg);

  let minBB = Infinity;
  let maxBB = -Infinity;

  const rowsOut: IAftBayLayoutRow[] = [];

  rows.forEach((row, idx) => {
    const tcg = perRow[row]?.tcg;
    if (tcg === undefined) return;

    const prevTcg = perRow[rows[idx - 1]]?.tcg;
    const nextTcg = perRow[rows[idx + 1]]?.tcg;

    const prevTcgMid = idx === 0 ? tcg - TCG_IN_MMM * 0.5 : ((prevTcg ?? tcg) + tcg) * 0.5;
    const nextTcgMid =
      idx === rows.length - 1 ? tcg + TCG_IN_MMM * 0.5 : ((nextTcg ?? tcg) + tcg) * 0.5;

    const rowTiers = tiersByRow[row];
    if (!rowTiers) return;

    const bottomBaseMm = perRow[row]?.bottomBase;
    if (bottomBaseMm === undefined) return;

    const maxHeightMm = perRow[row]?.maxHeight;

    const bottomTier = pad2(rowTiers.minTier) as ITierPattern;
    const topTier = pad2(rowTiers.maxTier) as ITierPattern;
    const numTiers = (Number(topTier) - Number(bottomTier)) * 0.5 + 1;

    const tierBasesMm: number[] = new Array(numTiers);
    let cumulativeHeight = 0;
    for (let t = 0; t < numTiers; t += 1) {
      tierBasesMm[t] = bottomBaseMm + cumulativeHeight;
      cumulativeHeight += getTierHeightMm(row, t);
    }

    if (maxHeightMm !== undefined && bottomBaseMm + maxHeightMm > maxBB) {
      maxBB = bottomBaseMm + maxHeightMm;
    }
    if (bottomBaseMm + cumulativeHeight > maxBB) {
      maxBB = bottomBaseMm + cumulativeHeight;
    }
    if (bottomBaseMm < minBB) minBB = bottomBaseMm;

    rowsOut.push({
      row,
      isPort: Number(row) % 2 === 0,
      tcg,
      prevTcgMid,
      nextTcgMid,
      bottomBaseMm,
      maxHeightMm,
      bottomTier,
      numTiers,
      tierBasesMm,
    });
  });

  return {
    widthMm,
    minBB,
    maxBB,
    addXMm,
    rows: rowsOut,
  };
}
